<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import * as amplitude from "@amplitude/analytics-browser";
import { fetchLocationFromApi } from "../utils/getLocation.js";
import { runningAsWechatMP } from "../utils/globalReferences.js";
import reportThirdPartyContent from "../utils/thirdPartyContentTrackingAPI.js";
import withTimeLimit from "../utils/asyncHelpers.js";

import * as Sentry from "@sentry/vue";
import SentryTransactionWrapper from "../utils/sentry.js";

import noSgImage from "../assets/3rd_party_code_no_sg_image.png";
import noStImage from "../assets/3rd_party_code_no_st_image.png";
import videoInstruction from "../assets/qr_3rd_party.mp4";

const __transaction__ = new SentryTransactionWrapper();

const version = __APP_VERSION__;
const props = defineProps({
  coordinates: Object,
  app_slug: String,
  install_id: String,
  device: String,
  device_header: String,
  os: String,
  os_version: String,
  toggle: Function,
  result: String,
  result_timestamp: Number
});

const i18nLocale = useI18n();
const videoElement = ref(null);
const uuid = ref("");
const location = ref(null);
const isLoadingReportData = ref(false);
/*
Available Whitelisted types:
wasmM.WhitelistType.Marketing
wasmM.WhitelistType.MarketingWithProtection
wasmM.WhitelistType.MarketingWithoutProtection
wasmM.WhitelistType.ExternalProvider
*/
const whitelistedUrl = computed(() => {
  if (props.result) {
    return !wasmM.checkValidUrl(props.result) || wasmM.checkUrlWhitelisted(props.result).type !== wasmM.WhitelistType.NotWhitelisted;
  }
});
/*
Available Blacklisted types:
wasmM.BlacklistType.Blacklisted_default
wasmM.BlacklistType.Blacklisted_dangerous
*/
const blacklistedUrl = computed(() => {
  if (props.result) {
    return wasmM.checkUrlBlacklisted(props.result).type !== wasmM.BlacklistType.NotBlacklisted;
  }
});

function close() {
  props.toggle(false);
  amplitude.track("Scan another QR code", window.amplitudeCommonProperties);
}

const report = async () => {
  const url = props.result;
  const fakeUrlReportFormId = import.meta.env.VITE_APP_FAKE_URL_REPORT_FORM_ID;
  const reportUrl = `https://scantrust.typeform.com/to/${fakeUrlReportFormId}#lang=${i18nLocale.locale.value}&userid=${props.install_id}&device=${props.device}&app_name=${props.app_slug}&app_version=${version}&url=${url}&scanid=${uuid.value}&latitude=${location.value.latitude}&longitude=${location.value.longitude}&country=${location.value.country}&source=${location.value.source}`;

  await withTimeLimit(amplitude.track("Report", window.amplitudeCommonProperties).promise.then(() => amplitude.flush()));
  window.location.href = reportUrl;
};

watch(
  () => props.result,
  async (resultUrl) => {
    isLoadingReportData.value = true;
    const amplitudeData = {
      url: resultUrl
    };
    // No redirection happens immediately after so we can increase the timeout (defaults 1s)
    await withTimeLimit(
      amplitude.track("NON_ST_CODE", { ...amplitudeData, ...window.amplitudeCommonProperties }).promise.then(() => amplitude.flush()),
      4000
    );

    location.value = await fetchLocationFromApi(props.coordinates);
    try {
      const type = blacklistedUrl.value ? "blacklist" : whitelistedUrl.value ? "whitelist" : "unknown";
      const resp = await reportThirdPartyContent(resultUrl, props.install_id, location.value, type, {
        appSlug: props.app_slug,
        deviceHeader: props.device_header
      });
      if (resp && resp.uuid) {
        uuid.value = resp.uuid;
      }
    } catch (e) {
      Sentry.captureException(new Error("Failed to track scan:", { cause: e }));
      console.error(e);
    } finally {
      isLoadingReportData.value = false;
    }
  }
);
const image = computed(() => (whitelistedUrl.value ? noSgImage : noStImage));

const showReportContainer = computed(() => !whitelistedUrl.value && !runningAsWechatMP.value);

const playVideo = async () => {
  videoElement.value.play().catch((error) => {
    console.error("Video failed to play:", error.message);
  });
};

defineExpose({
  playVideo
});
</script>

<template>
  <div class="text_all flex h-[calc(100dvh-70px)] flex-col gap-12 overflow-auto px-5 py-3">
    <div class="flex flex-col gap-5" :class="{ grow: whitelistedUrl || !runningAsWechatMP }">
      <div :class="[whitelistedUrl ? 'bg-[#FF7F00]' : 'bg-[#F22A42]']" class="flex min-h-[180px] justify-start rounded-[10px] px-3">
        <div class="relative mr-1 w-32 flex-none shrink-0">
          <img :src="image" alt="" class="absolute bottom-0 left-0 h-auto max-h-36 w-full object-contain" />
        </div>
        <div class="flex min-w-0 flex-1 flex-col justify-center text-white">
          <p class="mr-3 ml-2 text-[22px] font-bold break-word hyphens-auto">
            {{ blacklistedUrl ? $t("third_party_code.counterfeit") : $t("third_party_code.not_secure_code") }}
          </p>
        </div>
      </div>
      <div v-if="!blacklistedUrl" class="flex items-center justify-center gap-2.5 rounded-[10px] bg-[#FAFAFA] px-3 py-1.5">
        <p class="text-lg">
          {{ $t("third_party_code.authenticate_security_pattern") }}
        </p>
        <video ref="videoElement" class="h-[168px] w-full max-w-28" :src="videoInstruction" autoplay loop muted playsinline></video>
      </div>
    </div>
    <div class="mb-2 flex grow-0 flex-col items-center gap-4 text-center">
      <div
        v-if="showReportContainer"
        class="flex flex-col items-center gap-1.5"
      >
        <div class="text-[#F22A42]">
          <h3 v-if="!blacklistedUrl" class="text-lg font-bold">
            {{ $t("third_party_code.suspect") }}
          </h3>
          <p>{{ $t("third_party_code.report_to_us") }}</p>
        </div>
        <button
          class="max-h-[41px] min-w-[88px] rounded p-2.5 text-lg leading-[normal] font-bold text-white transition-colors"
          :class="isLoadingReportData ? 'bg-[#F22A42]/50' : 'bg-[#F22A42]'"
          :disabled="isLoadingReportData"
          @click="report"
        >
          {{ $t("third_party_code.report") }}
        </button>
      </div>
      <div class="mb-3 text-center">
        <button
          class="p-2.5 text-lg leading-[normal] font-normal text-[#5D5D5D] underline"
          @click="close"
        >
          {{ $t("third_party_code.scan") }}
        </button>
      </div>
    </div>
  </div>
</template>
